<template>
  <div class="px-4 px-md-0">
    <Preloader v-if="loading" fullscreen />
    <VAlert v-else-if="errorMessage" type="error">
      {{ errorMessage }}
    </VAlert>
    <div
      v-else
      class="vacancy-home-root"
      :class="{ 'vacancy-home-root--overlay-visible': isExpired && $route.name === 'candidates' }"
    >
      <div
        class="vacancy-home-main"
        :class="{ 'vacancy-home-main--blurred': isExpired && $route.name === 'candidates' }"
      >
      <VRow
        align="center"
        wrap
        no-gutters
        class="pt-4 pr-4 mb-4"
      >
        <VCol cols="12" md="auto" class="d-flex align-center">
          <h1 class="headline">
            {{ vacancy.title }}
          </h1>
          <VMenu offset-y transition="scale-transition">
            <template v-slot:activator="{ on }">
              <VBtn
                icon
                color="transparent"
                class="ml-2"
                v-on="on"
              >
                <svg
                  width="19"
                  height="11"
                  viewBox="0 0 19 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                ><path d="M9.87876 10.3634L18.8417 1.40048C19.0528 1.18945 19.0528 0.850998 18.8417 0.639965C18.6307 0.428931 18.2922 0.428931 18.0812 0.639965L9.5005 9.22068L0.919786 0.639964C0.708754 0.42893 0.370304 0.42893 0.159269 0.639964C0.0557442 0.74349 -1.75354e-08 0.882852 -2.34531e-08 1.01823C-2.93707e-08 1.15361 0.0517616 1.29297 0.159269 1.3965L9.12223 10.3595C9.32928 10.5705 9.67171 10.5705 9.87876 10.3634Z" fill="#333333" /></svg>
              </VBtn>
            </template>
            <VList class="py-0" dense>
              <VListItemGroup>
                <VListItem
                  v-for="v in notArchivedVacancies"
                  :key="v.id"
                  :to="menuTo(v)"
                >
                  <VListItemContent>
                    <VListItemSubtitle>{{ v.title }}</VListItemSubtitle>
                  </VListItemContent>
                </VListItem>
              </VListItemGroup>
            </VList>
          </VMenu>
        </VCol>
        <VCol v-if="vacancy.visibility === vacancyVisibility.INVITATION">
          <VacancyLockInfo />
        </VCol>
        <VMenu
          offset-y
          :close-on-content-click="false"
          z-index="500"
          min-width="320"
          v-model="shareMe"
        >
          <template v-slot:activator="{ on }">
            <VChip
              color="#fff"
              link
              class="px-0 px-md-2 copy-link"
              v-on="on"
              @click="copyLinkToClipBoard"
            >
              <VIcon small class="mr-1">
                mdi-link
              </VIcon>
              <span>
                <template>
                  {{ $t('copyLink') }}
                </template>
              </span>
            </VChip>
          </template>
          <VCard tile class="pa-5">
            <VBtn
              fab
              icon
              right
              top
              absolute
              x-small
              @click="shareMe = false"
              class="mt-3 mr-n5"
            >
              <VIcon>
                mdi-close
              </VIcon>
            </VBtn>
            <div>
              <span v-if="linkCopied">
                <VIcon color="success">mdi-check</VIcon>
                {{ $t('linkCopied') }}
              </span>
              <div>{{ vacancyUrl }}</div>
            </div>
          </VCard>
        </VMenu>
      </VRow>
      <VTabs
        background-color="transparent"
        color="dark"
        slider-size="2"
        slider-color="primary"
        :fixed-tabs="$vuetify.breakpoint.smAndDown"
      >
        <VTab
          exact
          class="mr-3 mr-md-12"
          :to="{ name: 'vacancy', params: { id: $route.params.id }}"
          :class="[$vuetify.breakpoint.mdAndUp ? 'title' : 'body-2 font-weight-bold']"
        >
          {{ $t('vacancy') }}
        </VTab>
        <VTab
          v-if="!isDraftVacancy"
          exact
          class="mr-3 mr-md-12"
          :to="{ name: 'candidates', params: { id: $route.params.id }}"
          :class="[$vuetify.breakpoint.mdAndUp ? 'title' : 'body-2 font-weight-bold']"
        >
          {{ $t('candidates') }}
        </VTab>
        <VTab
          v-if="!isDraftVacancy && hasVacancySettings"
          exact
          class="mr-3 mr-md-12"
          :to="{ name: 'vacancy-ai-processor-settings', params: { id: $route.params.id }}"
          :class="[$vuetify.breakpoint.mdAndUp ? 'title' : 'body-2 font-weight-bold']"
        >
          {{ $t('settings') }}
        </VTab>
      </VTabs>
      <RouterView />
      </div>
      <div
        v-if="isExpired && $route.name === 'candidates'"
        class="vacancy-home-tariff-overlay"
      >
        <VCard class="vacancy-home-tariff-card">
          <VCardTitle class="vacancy-home-tariff-header">
            <img
              :src="require('@/assets/img/tarrif-change-warning.svg')"
              alt=""
              class="vacancy-home-tariff-icon"
            >
            <div class="vacancy-home-tariff-title">
              {{ $t('tariffInactiveTitle') }}
            </div>
          </VCardTitle>
          <VCardText class="vacancy-home-tariff-text">
            {{ $t('tariffInactiveText') }}
          </VCardText>
          <VBtn
            class="vacancy-home-tariff-btn"
            color="success"
            @click="redirectToUpgradePlan"
          >
            {{ $t('tariffInactiveButton') }}
          </VBtn>
        </VCard>
      </div>

      <VDialog v-model="wsDeletedDialog" persistent max-width="600">
        <VCard>
          <DialogHeader
            :title="$t('vacancyRemoved')"
            text-class="error--text"
            @close="wsDeletedConfirm"
          />
          <VCardText class="pa-5">
            {{ $t('vacancyRemovedLong') }}
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn text color="primary" @click="wsDeletedConfirm">
              {{ $t('backToMainPage') }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
    </div>
  </div>
</template>

<script>
import { watch, computed, onMounted } from '@vue/composition-api';
import { vacancyVisibility, vacancyStatus, paymentTabs, settingsTabs } from '@/enums';
import useScrollLock from '@/compositions/useScrollLock';
import useSubscription from '@/compositions/accounting/useSubscription';
import VacancyLockInfo from '@/components/Vacancy/VacancyLockInfo';
import useVacancies from '@/compositions/useVacancies';
import useAccount from '@/compositions/accounting/useAccount';
import Company from '@/orm/models/Company/Company';
import Preloader from '../components/Preloader.vue';

import ValidationMixin from '../mixins/ValidationMixin';
import Vacancy from '../orm/models/Vacancy/Vacancy';
import DialogHeader from '../components/DialogHeader.vue';

export default {
  name: 'VacancyHome',
  components: { VacancyLockInfo, DialogHeader, Preloader },
  mixins: [ValidationMixin],
  setup(props, { root }) {
    const { account, fetchIfNull } = useAccount();
    const { isExpired } = useSubscription();

    onMounted(() => {
      fetchIfNull();
    });

    const vacancy = computed(() => Vacancy.query().find(root.$route.params.id));

    const hasVacancySettings = computed(() => {
      if (!vacancy.value) return false;
      return vacancy.value.hasHhIntegration === true;
    });

    function redirectToUpgradePlan() {
      root.$router.push({
        name: 'settings',
        params: { type: settingsTabs.PAYMENT, addType: paymentTabs.INFO },
      });
    }

    const isScrollLocked = computed(
      () => isExpired.value && root.$route.name === 'candidates',
    );
    useScrollLock(isScrollLocked);

    return { hasVacancySettings, isExpired, redirectToUpgradePlan };
  },
  data() {
    return {
      loading: true,
      error: null,
      errorMessage: null,
      wsDeletedDialog: false,
      vacancyVisibility,
      linkCopied: false,
      shareMe: false,
    };
  },
  computed: {
    company() {
      return Company.query().withAllRecursive().first();
    },
    vacancy() {
      return Vacancy.query().find(this.$route.params.id);
    },
    notArchivedVacancies() {
      return Vacancy.scopes.notArchivedNotDraftVacancies().all();
    },
    isDraftVacancy() {
      return this.vacancy.status === vacancyStatus.DRAFT;
    },
    isCopyLinkVisible() {
      return this.vacancy.status === vacancyStatus.ACTIVE;
    },
    vacancyUrl() {
      return `${process.env.VUE_APP_FRONT_URL}/v/${this.vacancy.id}`;
    },
  },
  watch: {
    '$route.params.id': 'fetchData',
    shareMe(val) {
      if (val) {
        this.linkCopied = true;
      }
    },
  },
  created() {
    this.$echo.private(`company.${this.company.id}`)
      .listen('.vacancy.destroyed', (data) => {
        if (data.id === this.vacancy.id) {
          this.wsDeletedDialog = true;
        }
      });
    this.fetchData();
  },
  methods: {
    async fetchData() {
      const { currentVacancy } = useVacancies();
      await this.withApiErrorHandling(async () => {
        await Promise.all([
          Vacancy.api().fetchById(this.$route.params.id),
          Vacancy.api().fetchAllByCompanyId(this.company.id),
        ]);
        currentVacancy.value = this.vacancy;
      }, (e) => {
        this.errorMessage = e.message;
      });
    },
    menuTo(vacancy) {
      if (this.$route.name === 'candidate') {
        return { name: 'candidates', params: { id: vacancy.id } };
      }
      return { name: this.$route.name, params: { id: vacancy.id } };
    },
    async wsDeletedConfirm() {
      this.wsDeletedDialog = false;
      const { id } = this.vacancy;
      await this.$router.push({ name: 'dashboard' });
      await Vacancy.delete(id);
    },
    copyLinkToClipBoard() {
      navigator.clipboard.writeText(this.vacancyUrl);
      this.linkCopied = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.vacancy-home-root {
  position: relative;

  &--overlay-visible {
    min-height: 100vh;
  }
}

.vacancy-home-main--blurred {
  filter: blur(4px);
}

.vacancy-home-tariff-overlay {
  position: absolute;
  top: -48px;
  left: -48px;
  right: -48px;
  bottom: -48px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background: rgba(0, 0, 0, 0.55);
}

.vacancy-home-tariff-card {
  max-width: 400px;
  width: 100%;
  padding: 30px;
  border-radius: 8px;
}

.vacancy-home-tariff-header {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  padding: 0;
  margin-bottom: 8px;
}

.vacancy-home-tariff-icon {
  padding: 13px;
  background: rgba(234, 13, 13, 0.1);
  border-radius: 50%;
  margin-bottom: 20px;
}

.vacancy-home-tariff-title {
  font-weight: 400;
  font-size: 24px;
  line-height: 26px;
  color: #212121;
}

.vacancy-home-tariff-text {
  padding: 0;
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 19.6px;
  font-weight: 300;
  color: #212121;
}

.vacancy-home-tariff-btn {
  width: 100%;
  font-weight: 400;
  font-size: 16px;
}

::v-deep {
  .v-tabs-bar {
    margin-bottom: 40px;
    @media screen and (max-width: 428px) {
      margin-bottom: 30px !important;
    }
  }
}

.copy-link {
  @media screen and (max-width: 599px) {
    margin-top: 5px;
  }
}
</style>

<i18n locale="en" lang="yaml">
  copyLink: Copy link
  copyLinkDescription: Link to share
  linkCopied: Link copied
  vacancy: Job
  candidates: Candidates
  settings: Settings
  backToMainPage: Return to dashboard
  vacancyRemoved: Job deleted
  vacancyRemovedLong: Job was deleted while you were working on it.
  tariffInactiveTitle: Your tariff is inactive.
  tariffInactiveText: To gain access to all the functions of your tariff, you need to make a payment.
  tariffInactiveButton: Upgrade plan
</i18n>

<i18n locale="ru" lang="yaml">
  copyLink: Копировать ссылку
  copyLinkDescription: Ссылка для соцсетей и мессенджеров
  linkCopied: Ссылка скопирована
  vacancy: Вакансия
  candidates: Кандидаты
  settings: Настройки
  backToMainPage: На главную
  vacancyRemoved: Вакансия удалена
  vacancyRemovedLong: Кто-то удалил эту вакансию пока вы с ней работали.
  tariffInactiveTitle: Тариф закончился
  tariffInactiveText: Для получения доступа к функциям Вашего тарифа необходимо внести оплату.
  tariffInactiveButton: Оплатить тариф
</i18n>
